import { selectAllServiceProducts } from '@user/store/serviceProducts/slice'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts'
import { useSelector } from 'react-redux'
import { useGetServiceFrequenciesOnMount } from '@user/store/serviceFrequency/hooks/useGetServiceFrequenciesOnMount'
import { selectAllServiceFrequencies } from '@user/store/serviceFrequency/slice'
import { ILeadOppProduct } from '@user/store/leadOppProducts/typings'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Props as ServiceProps } from '../Services'
import { useDialogState } from '@user/utils/useDialogState'
import { selectLeadById, selectLeadDetail } from '@user/store/lead'
import {
  selectOpportunityById,
  selectOpportunityDetail,
} from '@user/store/opportunity'
import { useAppSelector } from '@user/store/hooks'
import { ENTITY_TYPES } from '@sales-center/common'
import { getNextSortOrderByLocation } from '../AddServiceForm/hooks'
import { Id } from 'react-toastify'

type Props = Pick<
  ServiceProps,
  | 'entityId'
  | 'readOnly'
  | 'openAddServiceDrawerOnMount'
  | 'serviceLocationsLoading'
  | 'entityType'
  | 'serviceLocations'
>

export function useData(props: Props) {
  const {
    entityId,
    readOnly,
    openAddServiceDrawerOnMount,
    serviceLocationsLoading,
    entityType,
    serviceLocations,
  } = props
  const tabletView = useMediaQuery('(max-width:1000px)')
  const saveForLaterToastIdRef = React.useRef<Id | null>(null)

  const serviceProducts = useSelector(selectAllServiceProducts)
  const services = useSelector(selectAllLeadOppProducts)

  const totalServicesCount = services?.length

  const serviceFreqStatus = useGetServiceFrequenciesOnMount()
  const serviceFrequencies = useSelector(selectAllServiceFrequencies)

  const loading = serviceFreqStatus === 'pending' || serviceLocationsLoading

  const disabled =
    readOnly == true ||
    serviceFreqStatus !== 'success' ||
    serviceLocationsLoading

  const localMode = entityId === -1

  const lead = useAppSelector((state) => selectLeadById(state, entityId))
  const leadDetail = useAppSelector(selectLeadDetail)
  const opportunity = useAppSelector((state) =>
    selectOpportunityById(state, entityId),
  )
  const oppDetail = useAppSelector(selectOpportunityDetail)
  const isClosedEntity = useMemo(
    () =>
      !!(entityType == ENTITY_TYPES.Lead
        ? lead
          ? lead.closed
          : Boolean(leadDetail?.closedDate)
        : opportunity
          ? opportunity.closed
          : Boolean(oppDetail?.closedDate)),
    [
      entityType,
      lead,
      leadDetail?.closedDate,
      opportunity,
      oppDetail?.closedDate,
    ],
  )

  const nextSortOrderMap = useMemo(
    () => getNextSortOrderByLocation(localMode, serviceLocations, services),
    [localMode, serviceLocations, services],
  )

  const [open, setOpen] = useState(false)
  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  const [editService, setEditService] = useState<ILeadOppProduct>()
  const onEdit = useCallback((service: ILeadOppProduct) => {
    setEditService(service)
    setOpen(true)
  }, [])

  useEffect(() => {
    if (!open) setEditService(undefined)
  }, [open])

  useEffect(() => {
    if (!loading && openAddServiceDrawerOnMount) setOpen(true)
  }, [loading, openAddServiceDrawerOnMount])

  const [saveForLaterOpen, onOpenSaveForLater, onCloseSaveForLater] =
    useDialogState(false)

  return {
    disabled,
    editService,
    loading,
    localMode,
    onClose,
    onEdit,
    onOpen,
    onCloseSaveForLater,
    onOpenSaveForLater,
    open,
    saveForLaterOpen,
    serviceFrequencies,
    serviceProducts,
    tabletView,
    isClosedEntity,
    totalServicesCount,
    nextSortOrderMap,
    saveForLaterToastIdRef,
  } as const
}

export type UseData = ReturnType<typeof useData>
